import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";

import {
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Badge,
  Grid,
  Popover,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";

import * as Icons from "@mui/icons-material";

import Joyride, { EVENTS } from "react-joyride";

import { linksData } from "../../routes";
import { Link } from "react-router-dom";
import { AuthenticationContext } from "../../auth/AuthProvider";

import { backend, API_URL, DEBUG } from "../../config";

import { useDispatch, useSelector } from "react-redux";
import { initialize_cart_action } from "./../../redux/actions/cart";
import { set_current_user_action } from "./../../redux/actions/current_user";
import { set_customer_action } from "./../../redux/actions/customer";

const tourConfigureOrderGuide = [
  {
    target: ".menu-button",
    title: "Open navigation sidebar",
    content: "You can navigate through different sections of the application.",
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".order-guide",
    title: "Go to My Order Guide",
    content:
      "Personalize your order guide, you can view and manage your categories and products added to your categories.",
    isFixed: true,
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: "body",
    placement: "center",
    title: "Loading Your Order Guide",
    content:
      "It could take a few seconds to load your order guide, it is advised to wait until you see your categories before moving to next step",
    disableBeacon: true,
  },
  {
    target: ".new-category",
    title: "Add New Category",
    content: (
      <>
        Use this section to add a new category to your order guide.
        <br />
        <br />
        <div
          style={{
            background: "lightyellow",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Warning"])}
          </div>
          <div
            style={{ fontSize: 14, textAlign: "left", whiteSpace: "pre-line" }}
          >
            You can create as many categories and add products to them.
            <br />
            <b>Make sure to save your changes before leaving.</b>
          </div>
        </div>
      </>
    ),
    disableBeacon: true,
  },
  {
    target: ".products-tab",
    placement: "bottom",
    title: "Go to Products Tab",
    content:
      "In the products tab, you can search for products and add them to your order guide categories.",
    disableBeacon: true,
    disableScrolling: true,
  },
  {
    target: ".new-product",
    title: "Add New Product to Category",
    content: (
      <>
        Use this section to add a new product to an order guide category.
        <br />
        <br />
        <div
          style={{
            background: "lightyellow",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Warning"])}
          </div>
          <div style={{ fontSize: 14, textAlign: "left" }}>
            You can add as many products to your categories as you like.
            <br />
            <b>Make sure to save your changes before leaving.</b>
          </div>
        </div>
      </>
    ),
    disableBeacon: true,
  },
  {
    target: ".save-button",
    title: "Save Changes",
    content:
      "Use this button to confirm and save your changes to the order guide.",
    disableBeacon: true,
    hideFooter: true,
  },
];

const tourSearchItemAdd = [
  {
    target: ".search-form",
    title: "Search for Products",
    content:
      "Use this search form to find products and add them to your cart or order guide.",
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".add-to-guide-button",
    title: "Open Add to Order Guide form",
    content:
      "Click to open a form where you can select a category and add this item to your order guide.",
    disableBeacon: true,
  },
  {
    target: ".assign-orderlist-dialog .MuiDialog-paper",
    title: "Add product to Order Guide",
    content: (
      <>
        Use this form to add a new product to an order guide category. Select
        the category you want to add the product to and click Save.
        <br />
        <br />
        <div
          style={{
            background: "lightblue",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Info"])}
          </div>
          <div style={{ fontSize: 14, textAlign: "left" }}>
            You can also navigate to your order guide clicking the icon{" "}
            {React.createElement(Icons["OpenInNew"], {
              size: "small",
              style: { verticalAlign: "middle" },
            })}{" "}
            icon.
            <br />
            <b>This action will open your order guide in a new tab.</b>
          </div>
        </div>
      </>
    ),
    disableBeacon: true,
    hideFooter: true,
  },
];

const tourCreateOrder = [
  {
    target: ".menu-button",
    title: "Open navigation sidebar",
    content: "You can navigate through different sections of the application.",
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".orders-link",
    title: "Go to Orders",

    content: (
      <>
        You can view your orders, create a new order, access order and invoice
        details, replicate an order.
        <br />
        <br />
        <div
          style={{
            background: "lightyellow",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Warning"])}
          </div>
          <div
            style={{ fontSize: 14, textAlign: "left", whiteSpace: "pre-line" }}
          >
            Make sure you have already configured some items in your order guide
            otherwise no products will appear when creating an order.
          </div>
        </div>
      </>
    ),
    isFixed: true,
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".create-order-button",
    title: "Head to Create Order Page",
    content:
      "You can navigate through your order guide and pick products to add to your order.",
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".card-item",
    title: "Add a Unit or Case to your Cart",
    content: (
      <>
        <Typography variant="body2">
          Use the + button on the product cards to add either a unit or a case
          of the product to your cart.
          <br />
          <br />
          Use the - button to remove units or cases from your cart.
          <br />
          <br />
          Additionally, you can type in the quantity you want to add to your
          cart directly in the input field.
          <br />
          <br />
        </Typography>
        <div
          style={{
            background: "lightblue",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Info"])}
          </div>
          <div
            style={{ fontSize: 14, textAlign: "left", whiteSpace: "pre-line" }}
          >
            You can add as many products as you like.
          </div>
        </div>
      </>
    ),
    disableBeacon: true,
  },
  {
    target: ".cart-button",
    title: "Proceed to Cart Checkout",
    content: (
      <>
        Proceed to the cart to review your selected products, make any necessary
        adjustments, and complete your order.
        <br />
        <br />
        <div
          style={{
            background: "lightblue",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Info"])}
          </div>
          <div
            style={{ fontSize: 14, textAlign: "left", whiteSpace: "pre-line" }}
          >
            You can also use the staged order to place an order, you can save
            your cart items in a staged order during Cart Checkout.
            <br />
            <b>Note:</b> Your normal cart is saved locally on your browser, if
            you switch devices or clear your browser data, your cart will be
            lost. Staged orders are saved on our servers and can be accessed
            from any device by logging into your account.
          </div>
        </div>
      </>
    ),
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".place-order-button",
    title: "Complete and confirm your order",

    content: (
      <>
        Complete any further necessary steps to place your order, such as
        entering payment if applicable, and confirm your order.
        <br />
        <br />
        <div
          style={{
            background: "lightyellow",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Warning"])}
          </div>
          <div
            style={{ fontSize: 14, textAlign: "left", whiteSpace: "pre-line" }}
          >
            If you don't follow through all the process your order might not be
            successfully placed. Make sure to complete all the steps, you will
            be redirected to your order list after placing your order
            successfully.
          </div>
        </div>
      </>
    ),
    isFixed: true,
    disableBeacon: true,
    hideFooter: true,
  },
];

const tourViewInvoice = [
  {
    target: ".menu-button",
    title: "Open navigation sidebar",
    content: "You can navigate through different sections of the application.",
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".orders-link",
    title: "Go to Orders",
    content: (
      <>
        You can view your orders, create a new order, access order and invoice
        details, replicate an order.
        <br />
        <br />
        <div
          style={{
            background: "lightyellow",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Warning"])}
          </div>
          <div
            style={{ fontSize: 14, textAlign: "left", whiteSpace: "pre-line" }}
          >
            Make sure you have already configured some items in your order guide
            otherwise no products will appear when creating an order.
          </div>
        </div>
      </>
    ),
    isFixed: true,
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".edit-link",
    title: "Open Order details page",
    content:
      "You can view your order details such as products, quantities, prices, and invoice details if available.",
    disableBeacon: true,
    disableScrolling: true,
    hideFooter: true,
  },
  {
    target: ".view-invoice-button",
    title: "Go to Invoice details",
    content: (
      <>
        You can view your invoice details such as products, quantities, prices,
        applied weights, fees, and totals.
        <br />
        <br />
        <div
          style={{
            background: "lightblue",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Info"])}
          </div>
          <div style={{ fontSize: 14, textAlign: "left" }}>
            You can also pay your invoice if payments are enabled.
          </div>
        </div>
      </>
    ),
    disableBeacon: true,
    hideFooter: true,
  },
];

const tourReplicateOrder = [
  {
    target: ".menu-button",
    title: "Open navigation sidebar",
    content: "You can navigate through different sections of the application.",
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".orders-link",
    title: "Go to Orders",

    content: (
      <>
        You can view your orders, create a new order, access order and invoice
        details, replicate an order.
        <br />
        <br />
        <div
          style={{
            background: "lightyellow",
            display: "flex",
            gap: 8,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.createElement(Icons["Warning"])}
          </div>
          <div
            style={{ fontSize: 14, textAlign: "left", whiteSpace: "pre-line" }}
          >
            Make sure you have already configured some items in your order guide
            otherwise no products will appear when creating an order.
          </div>
        </div>
      </>
    ),
    isFixed: true,
    disableBeacon: true,
    hideFooter: true,
  },
  {
    target: ".edit-link",
    title: "Open Order details page",
    content:
      "You can view your order details such as products, quantities, prices, and invoice details if available.",
    disableBeacon: true,
    disableScrolling: true,
    hideFooter: true,
  },
  {
    target: ".replicate-order-button",
    title: "Click to replicate this order",
    content:
      "The products from this order will be added to your cart after confirmation.",
    disableBeacon: true,
    hideFooter: true,
  },
];

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  toolbar: {
    backgroundColor: "#b35a4c",
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    backgroundColor: "#c88a7d !important",
    width: drawerWidth,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
    maxWidth: "100%",
    /* background: "#c88a7b !important" */
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  menuItems: {
    textDecoration: "none",
    color: theme.palette.text.secondary,
  },
  noDecoration: {
    textDecoration: "none !important",
  },
  devWatermark: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    overflow: "hidden",
    zIndex: 0,
    opacity: 0.06,
    fontSize: "200px",
    fontWeight: 900,
    color: "#FFD700",
    lineHeight: "250px",
    transform: "rotate(-45deg)",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
  },
  tutorialHintWrap: {
    position: "relative",
    display: "inline-flex",
    marginRight: theme.spacing(1),
  },
  tutorialHintButton: {
    position: "relative",
    overflow: "visible",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: -4,
      borderRadius: "50%",
      border: "2px solid rgba(255, 215, 0, 0.75)",
      boxShadow: "0 0 0 0 rgba(255, 215, 0, 0.45)",
      animation: "$tutorialPulse 2.2s ease-out infinite",
    },
  },
  tutorialHintBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ffd54f",
      color: "#2d1d00",
      fontSize: "0.65rem",
      minWidth: 22,
      height: 20,
      padding: "0 6px",
      border: "1px solid rgba(255,255,255,0.85)",
      boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
    },
  },
  "@keyframes tutorialPulse": {
    "0%": {
      transform: "scale(0.95)",
      opacity: 0.85,
      boxShadow: "0 0 0 0 rgba(255, 215, 0, 0.45)",
    },
    "70%": {
      transform: "scale(1.08)",
      opacity: 0,
      boxShadow: "0 0 0 12px rgba(255, 215, 0, 0)",
    },
    "100%": {
      transform: "scale(1.08)",
      opacity: 0,
      boxShadow: "0 0 0 0 rgba(255, 215, 0, 0)",
    },
  },
}));

// todo: add storefront in case its globally accesible and remove from permission checks (customer_editable_orderlist_enabled)
const customerAllowedRoutes = ["order", "orders", "cart", "products"];

const AppNavBar = (props) => {
  const { children } = props;
  const path = props?.location?.pathname;
  const classes = useStyles();
  const theme = useTheme();
  const timerRef = useRef(null);
  const dispatch = useDispatch();
  const cartStore = useSelector((state) => state.cart);
  const currentUserStore = useSelector((state) => state.current_user);
  const customerStore = useSelector((state) => state.customer);

  const printMode =
    props.location.pathname.includes("print") &&
    !props.location.pathname.includes("printPicklist");

  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorTours, setAnchorTours] = useState(null);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [flattenedCategories, setFlattenedCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [startTour, setStartTour] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const [selectedTourSteps, setSelectedTourSteps] = useState([]);
  const [tourStepTry, setTourStepTry] = useState(0);
  const [showTutorialHint, setShowTutorialHint] = useState(false);

  const openCart = Boolean(anchorEl);
  const openTours = Boolean(anchorTours);

  let auth = useContext(AuthenticationContext);

  const handleClickTours = (event) => {
    setAnchorTours(event.currentTarget);

    if (showTutorialHint) {
      setShowTutorialHint(false);
    }
  };
  const handleCloseTours = () => setAnchorTours(null);
  const handleClickStartTour = (tour) => {
    if (!tour?.name || !tour?.steps) return;
    setStepIndex(0);

    if (["tourSearchItemAdd"].includes(tour.name)) {
      if (path.includes("products")) {
        setStepIndex(1);
      }
    }

    if (
      [
        "tourConfigureOrderGuide",
        "tourCreateOrder",
        "tourViewInvoice",
        "tourReplicateOrder",
      ].includes(tour.name)
    ) {
      const ordersRegex = /^\/order\/(\d+)\/?$/;

      if (path.includes("cart") && tour.name === "tourCreateOrder") {
        setStepIndex(4);
      } else if (
        ["tourViewInvoice", "tourReplicateOrder"].includes(tour.name) &&
        ordersRegex.test(path)
      ) {
        setStepIndex(3);
      } else if (
        ["tourCreateOrder", "tourViewInvoice", "tourReplicateOrder"].includes(
          tour.name,
        ) &&
        path.includes("orders")
      ) {
        setStepIndex(2);
      } else if (open) {
        setStepIndex(1);
      }
    }

    if (["tourConfigureOrderGuide"].includes(tour.name)) {
      if (path.includes("orderlists")) {
        const ele = document.querySelector(".new-product");
        if (ele) {
          setStepIndex(5);
        } else {
          setStepIndex(3);
        }
      }
    }

    setSelectedTour(tour.name);
    setSelectedTourSteps(tour.steps);
    setStartTour(true);
    setTourStepTry(0);
    handleCloseTours();
  };

  const handleJumpToNextStep = (timing = 200) => {
    setTimeout(() => {
      setStepIndex((prev) => prev + 1);
    }, timing);
  };

  const handleFinishTour = () => {
    setStartTour(false);
    setStepIndex(0);
    setTourStepTry(0);
    setSelectedTour(null);
    setSelectedTourSteps([]);
    setAnchorTours(null);
  };

  const handleTourCallback = (data) => {
    const { action, index, status, type } = data;

    if (type === EVENTS.TARGET_NOT_FOUND && tourStepTry < 1) {
      setStartTour(false);

      if (selectedTour === "tourSearchItemAdd" && stepIndex === 2) {
        const btn = document.querySelector(".add-to-guide-button");
        if (btn) btn.click();
      }

      if (selectedTour === "tourConfigureOrderGuide") {
        const btn = document.querySelector(".products-tab");
        if (btn) btn.click();
      }

      timerRef.current = setTimeout(() => {
        setStartTour(true);
        setTourStepTry((prev) => prev + 1);
      }, 500);

      return;
    }

    if (
      type === EVENTS.TARGET_NOT_FOUND &&
      tourStepTry >= 1 &&
      index === selectedTourSteps.length - 1
    ) {
      handleFinishTour();
      return;
    }

    if (index === selectedTourSteps.length - 1) {
      timerRef.current = setTimeout(() => {
        handleFinishTour();
      }, 8000);
      return;
    }

    if (
      type === EVENTS.TARGET_NOT_FOUND ||
      action === "close" ||
      ["finished", "skipped"].includes(status)
    ) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      handleFinishTour();
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      setStepIndex(index + (action === "prev" ? -1 : 1));
      return;
    }

    return;
  };

  const handleClickCart = (event) => setAnchorEl(event.currentTarget);
  const handleCloseCart = () => setAnchorEl(null);
  const handleDrawer = () => {
    setOpen(!open);

    if (!open && startTour) {
      handleJumpToNextStep();
    }
  };

  const handleLogout = () => {
    dispatch(set_customer_action(null));

    auth.logout();
  };

  const handleCustomerChange = (e, v) => {
    if (!Object.keys(cartStore).includes("" + v.id)) {
      dispatch(initialize_cart_action([], v));
    }
    dispatch(set_customer_action(v));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchQuery(value);
  };

  const handleCategoryChange = (e, value) => {
    // todo: remove when Shai figures out categories
    // setSelectedCategory(value);
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const params = new URLSearchParams();
    if (searchQuery) params.append("search", searchQuery);
    if (selectedCategory) params.append("category", selectedCategory.id);

    setSelectedCategory(null);
    setSearchQuery("");

    props.history.push(
      `/products${params.toString() ? `?${params.toString()}` : ""}`,
    );
  };

  // Permission check for menu nav
  const checkPermissions = (routeUrl) => {
    if (!currentUserStore) return false;

    if (
      routeUrl === "legacySync" &&
      currentUserStore.role_id < 1 &&
      currentUserStore.role_id > 11
    )
      return false;

    if (currentUserStore.is_staff) return true;

    if (currentUserStore.role_id === 14) {
      return ![
        "customers",
        "receiptDifference",
        "users",
        "priceRefresh",
        "setup",
        "legacySync",
        "orderlistPrototype",
        "emailInvoices",
        "orderlistPublic",
      ].includes(routeUrl);
    }

    if (routeUrl === "storefront")
      return Boolean(
        customerStore?.company_location_data?.[0]
          ?.customer_editable_orderlist_enabled,
      );

    return ![
      "inventory",
      "customers",
      "receiptDifference",
      "users",
      "priceRefresh",
      "setup",
      "legacySync",
      "orderlistPrototype",
      "emailInvoices",
      "orderlistPublic",
      "reporting",
      "itemcodes",
      "printPicklists",
    ].includes(routeUrl);
  };

  // Permission check for path
  const checkPermissionsPath = (pathUrl) => {
    /**
     * All storefront related and sidebar categories were commented out until Shai figures out the categories
     */

    if (!currentUserStore) return false;

    if (currentUserStore.is_staff) return true;

    // todo: remove when Shai figures out the categories
    if (pathUrl.includes("storefront")) return false;

    if (pathUrl === "/" || pathUrl.includes("verify")) return true;

    /* if (pathUrl.includes("storefront"))
      return Boolean(
        customerStore?.company_location_data?.[0]
          ?.customer_editable_orderlist_enabled,
      ); */

    if (pathUrl.includes("customer") && pathUrl.includes("orderlists")) {
      if (customerStore?.company_location?.length > 0)
        return (
          Boolean(
            customerStore?.company_location_data?.[0]
              ?.customer_editable_orderlist_enabled,
          ) &&
          customerStore.company_location.some(
            (clId) => pathUrl === `/customer/${clId}/orderlists`,
          )
        );

      return false;
    }

    return customerAllowedRoutes.some((route) => pathUrl.includes(route));
  };

  const navigationSteps = useCallback(() => {
    const ordersRegex = /^\/order\/(\d+)\/?$/;

    if (
      ["tourCreateOrder", "tourViewInvoice", "tourReplicateOrder"].includes(
        selectedTour,
      )
    ) {
      if (
        (path.includes("orders/add") && stepIndex === 2) ||
        (path.includes("orders") && stepIndex === 1)
      ) {
        handleJumpToNextStep();
      }

      if (ordersRegex.test(path) && stepIndex === 2) {
        handleJumpToNextStep();
      }

      if (path.includes("cart") && stepIndex === 4) {
        handleJumpToNextStep();
      }
    }

    if (selectedTour === "tourSearchItemAdd") {
      if (path.includes("products") && stepIndex === 0) {
        handleJumpToNextStep();
      }
    }

    if (selectedTour === "tourConfigureOrderGuide") {
      if (path.includes("orderlists") && stepIndex === 1) {
        handleJumpToNextStep();
      }
    }
  }, [path, selectedTour, stepIndex]);

  useEffect(() => {
    navigationSteps();
  }, [path, navigationSteps]);

  useEffect(() => {
    const companyLocationsGet = async () => {
      await backend
        .get(`${API_URL}companylocations?no_page=1`)
        .then(({ data }) => {
          setCompanyLocations(data);
        });
    };

    timerRef.current = setTimeout(() => {
      setShowTutorialHint(true);
    }, 2500);

    companyLocationsGet();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const flattenCategories = (categories, depth = 0, parentPath = "") => {
      let flattened = [];

      categories
        .filter((category) => category.active)
        .forEach((category) => {
          const fullPath = parentPath
            ? `${parentPath} > ${category.name}`
            : category.name;

          flattened.push({
            id: category.id,
            name: category.name,
            depth,
            fullPath,
            parent_id: category.parent_id || null,
            active: category.active,
          });

          if (category.children && category.children.length > 0) {
            flattened = [
              ...flattened,
              ...flattenCategories(category.children, depth + 1, fullPath),
            ];
          }
        });

      return flattened;
    };

    const categoriesGet = async () => {
      await backend
        .get(`${API_URL}productcategories?no_page=1`)
        .then(({ data }) => {
          const flattened = flattenCategories(data);
          setFlattenedCategories(flattened);
        })
        .catch((error) => {
          console.error("Error fetching categories:", error);
        });
    };

    categoriesGet();
  }, []);

  useEffect(() => {
    const currentUserSet = (users) => {
      const authUser = JSON.parse(auth.user);
      const currentUser =
        users.find((item) => item.username === authUser.user.username) || null;

      dispatch(set_current_user_action(currentUser));
    };

    const customersGet = async () => {
      await backend.get(`${API_URL}users?no_page=1`).then(({ data }) => {
        currentUserSet(data);
        const dataMap = data.map((item) => {
          const customerCompanyLocations = item.company
            ? companyLocations.filter(
                (clItem) => clItem.company_id === item.company,
              )
            : [];

          const customerCompanyLocationsId = customerCompanyLocations.map(
            (clItem) => clItem.id,
          );

          return {
            id: item.id,
            name: item.username,
            company_id: item.company,
            company_location: customerCompanyLocationsId,
            company_location_data: customerCompanyLocations,
          };
        });

        if (dataMap.length === 1) {
          const customerDefault = dataMap[0];

          if (!Object.keys(cartStore).includes("" + customerDefault.id)) {
            dispatch(initialize_cart_action([], customerDefault));
          }

          dispatch(set_customer_action(customerDefault));
        }

        setCustomers(dataMap);
      });
    };

    if (companyLocations.length) {
      customersGet();
    }
  }, [auth, cartStore, companyLocations, dispatch]);

  return (
    <div
      className={classes.root}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          handleDrawer();
        }
      }}
    >
      <Joyride
        continuous
        run={startTour}
        steps={selectedTourSteps}
        stepIndex={stepIndex}
        spotlightClicks={true}
        disableOverlayClose={true}
        overlayClickAction={false}
        callback={handleTourCallback}
        scrollOffset={80}
        styles={{
          options: {
            zIndex: 10000,
          },
        }}
        disableScrollParentFix={true}
        scrollToFirstStep={false}
        hideBackButton={true}
      />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
        style={{
          display: printMode ? "none" : "inherit",
        }}
      >
        <Toolbar className={classes.toolbar}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawer}
            edge="start"
            className={clsx(
              classes.menuButton,
              open && classes.hide,
              "menu-button",
            )}
          >
            {React.createElement(Icons["Menu"])}
          </IconButton>
          <Stack spacing={1} direction="row" flexGrow={1} alignItems="center">
            {DEBUG && (
              <Chip
                label="DEV"
                size="small"
                sx={{
                  backgroundColor: "#FFD700",
                  color: "#000",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  border: "2px solid rgba(255,255,255,0.9)",
                  "&:hover": {
                    backgroundColor: "#FFC700",
                    boxShadow: "0 3px 12px rgba(0,0,0,0.4)",
                  },
                }}
              />
            )}
            <Typography variant="h6" noWrap>
              FF AIO Engine
            </Typography>
            {currentUserStore?.is_staff ||
            customerStore?.company_location_data?.[0]
              ?.customer_editable_orderlist_enabled ? (
              <form onSubmit={handleSearch}>
                <Stack
                  spacing={1}
                  direction="row"
                  flexGrow={1}
                  alignItems="center"
                  className="search-form"
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Search"
                    name="search"
                    hiddenLabel
                    onChange={handleSearchChange}
                    value={searchQuery}
                    placeholder="Search products..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: "rgba(255, 255, 255, 0.95)",
                        background: "rgba(255, 255, 255, 0.12)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.25)",
                        borderRadius: "4px",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "rgba(255, 255, 255, 0.18)",
                          border: "1px solid rgba(255, 255, 255, 0.4)",
                        },
                        "&.Mui-focused": {
                          background: "rgba(255, 255, 255, 0.2)",
                          border: "1px solid rgba(255, 255, 255, 0.6)",
                          boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
                        },
                      },
                      "& .MuiOutlinedInput-input::placeholder": {
                        color: "rgba(255, 255, 255, 0.5)",
                        opacity: 1,
                      },
                    }}
                  />
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 220, display: "none" }}
                    options={flattenedCategories}
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        key={option.id}
                        style={{
                          paddingLeft: `${16 + option.depth * 20}px`,
                          color:
                            option.depth === 0
                              ? "#000"
                              : option.depth === 1
                                ? "#333"
                                : "#666",
                          fontSize: `${14 - option.depth * 1}px`,
                          fontWeight:
                            option.depth === 0
                              ? 600
                              : option.depth === 1
                                ? 500
                                : 400,
                        }}
                      >
                        <span
                          style={{
                            marginRight: 8,
                            opacity: 0.6,
                            fontWeight: 400,
                          }}
                        >
                          {option.depth === 1
                            ? "→ "
                            : option.depth === 2
                              ? "↳ "
                              : ""}
                        </span>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Department"
                        placeholder="Filter by department"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            color: "rgba(255, 255, 255, 0.95)",
                            background: "rgba(255, 255, 255, 0.12)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255, 255, 255, 0.25)",
                            borderRadius: "4px",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background: "rgba(255, 255, 255, 0.18)",
                              border: "1px solid rgba(255, 255, 255, 0.4)",
                            },
                            "&.Mui-focused": {
                              background: "rgba(255, 255, 255, 0.2)",
                              border: "1px solid rgba(255, 255, 255, 0.6)",
                              boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
                            },
                          },
                          "& .MuiOutlinedInput-input::placeholder": {
                            color: "rgba(255, 255, 255, 0.5)",
                            opacity: 1,
                          },
                        }}
                      />
                    )}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                  />
                  <IconButton
                    color="inherit"
                    aria-label="Search"
                    onClick={handleSearch}
                    type="submit"
                  >
                    {React.createElement(Icons["Search"])}
                  </IconButton>
                </Stack>
              </form>
            ) : (
              <></>
            )}
          </Stack>
          <div className={classes.tutorialHintWrap}>
            <Badge
              overlap="circular"
              badgeContent={showTutorialHint ? "New" : 0}
              invisible={!showTutorialHint}
              className={
                showTutorialHint ? classes.tutorialHintBadge : undefined
              }
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <IconButton
                color="inherit"
                onClick={handleClickTours}
                className={
                  showTutorialHint ? classes.tutorialHintButton : undefined
                }
                aria-label="Open tutorials"
              >
                {React.createElement(Icons["School"])}
              </IconButton>
            </Badge>
          </div>
          <Popover
            anchorEl={anchorTours}
            open={openTours}
            onClose={handleCloseTours}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <div
              style={{ padding: "6px 0px", minWidth: 250, textAlign: "center" }}
            >
              <Typography
                variant="subtitle1"
                style={{ padding: "6px 16px", fontWeight: "bold" }}
              >
                Tutorials
              </Typography>
              <Stack direction="column" style={{ padding: "6px 16px" }}>
                {!currentUserStore?.is_staff && (
                  <Stack direction="row" spacing={0} alignItems="center">
                    <div
                      style={{
                        display: "flex",
                        flexGrow: 1,
                      }}
                    >
                      <Typography variant="subtitle1">
                        Customize my order guide
                      </Typography>
                    </div>
                    <IconButton
                      color="primary"
                      variant="contained"
                      onClick={() =>
                        handleClickStartTour({
                          name: "tourConfigureOrderGuide",
                          steps: tourConfigureOrderGuide,
                        })
                      }
                      style={{ alignSelf: "flex-end" }}
                      aria-label="Start tutorial: Customize my order guide"
                    >
                      {React.createElement(Icons["PlayCircle"])}
                    </IconButton>
                  </Stack>
                )}
                <Stack direction="row" spacing={0} alignItems="center">
                  <div
                    style={{
                      display: "flex",
                      flexGrow: 1,
                    }}
                  >
                    <Typography variant="subtitle1">
                      Search products + Add to order guide
                    </Typography>
                  </div>
                  <IconButton
                    color="primary"
                    variant="contained"
                    onClick={() =>
                      handleClickStartTour({
                        name: "tourSearchItemAdd",
                        steps: tourSearchItemAdd,
                      })
                    }
                    aria-label="Start tutorial: Search products + Add to order guide"
                  >
                    {React.createElement(Icons["PlayCircle"])}
                  </IconButton>
                </Stack>
                {!currentUserStore?.is_staff && (
                  <Stack direction="row" spacing={0} alignItems="center">
                    <div
                      style={{
                        display: "flex",
                        flexGrow: 1,
                      }}
                    >
                      <Typography variant="subtitle1">
                        Create an Order
                      </Typography>
                    </div>
                    <IconButton
                      color="primary"
                      variant="contained"
                      onClick={() =>
                        handleClickStartTour({
                          name: "tourCreateOrder",
                          steps: tourCreateOrder,
                        })
                      }
                      aria-label="Start tutorial: Create an Order"
                    >
                      {React.createElement(Icons["PlayCircle"])}
                    </IconButton>
                  </Stack>
                )}
                <Stack direction="row" spacing={0} alignItems="center">
                  <div
                    style={{
                      display: "flex",
                      flexGrow: 1,
                    }}
                  >
                    <Typography variant="subtitle1">View an Invoice</Typography>
                  </div>
                  <IconButton
                    color="primary"
                    variant="contained"
                    onClick={() =>
                      handleClickStartTour({
                        name: "tourViewInvoice",
                        steps: tourViewInvoice,
                      })
                    }
                    aria-label="Start tutorial: View an Invoice"
                  >
                    {React.createElement(Icons["PlayCircle"])}
                  </IconButton>
                </Stack>
                <Stack direction="row" spacing={0} alignItems="center">
                  <div
                    style={{
                      display: "flex",
                      flexGrow: 1,
                    }}
                  >
                    <Typography variant="subtitle1">
                      Replicate a previous Order
                    </Typography>
                  </div>
                  <IconButton
                    color="primary"
                    variant="contained"
                    onClick={() =>
                      handleClickStartTour({
                        name: "tourReplicateOrder",
                        steps: tourReplicateOrder,
                      })
                    }
                    aria-label="Start tutorial: Replicate a previous Order"
                  >
                    {React.createElement(Icons["PlayCircle"])}
                  </IconButton>
                </Stack>
              </Stack>
            </div>
          </Popover>
          <IconButton
            color="inherit"
            onClick={handleClickCart}
            style={{ marginRight: 8 }}
          >
            <Badge
              badgeContent={
                customerStore && cartStore[customerStore.id]
                  ? cartStore[customerStore.id].products.length
                  : 0
              }
              color="primary"
            >
              {React.createElement(Icons["ShoppingCart"])}
            </Badge>
          </IconButton>
          <Popover
            anchorEl={anchorEl}
            open={openCart}
            onClose={handleCloseCart}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <div
              style={{ padding: "6px 0px", minWidth: 250, textAlign: "center" }}
            >
              {customerStore ? (
                <>
                  <FormControl
                    fullWidth
                    style={{ marginTop: 16, padding: "6px 16px" }}
                  >
                    <InputLabel shrink id="sorting-label">
                      Customer
                    </InputLabel>
                    <Autocomplete
                      fullWidth
                      disableClearable
                      name="customer"
                      options={customers}
                      getOptionLabel={(option) =>
                        option !== null ? `${option.name}` : ""
                      }
                      renderInput={(params) => (
                        <TextField {...params} variant="standard" />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={customerStore}
                      onChange={(e, value) => handleCustomerChange(e, value)}
                    />
                  </FormControl>
                  {cartStore[customerStore.id] &&
                  cartStore[customerStore.id].products.length > 0 ? (
                    <>
                      <Grid
                        container
                        style={{ padding: "8px 16px" }}
                        onClick={handleCloseCart}
                      >
                        <Grid item xs={12}>
                          <Stack
                            direction="row"
                            spacing={2}
                            justifyContent="center"
                          >
                            <Link to={`/cart`}>
                              <Button variant="contained" color="success">
                                <Typography variant="subtitle2">
                                  View Cart
                                </Typography>
                              </Button>
                            </Link>
                            <Link to={`/orders/add`}>
                              <Button variant="contained" color="inherit">
                                <Typography variant="subtitle2" color="black">
                                  Continue Shopping
                                </Typography>
                              </Button>
                            </Link>
                          </Stack>
                        </Grid>
                        {/* <Grid
                          item
                          xs={12}
                          sm={12}
                          component={Link}
                          to="/cart"
                          style={{
                            textAlign: "center",
                            textDecoration: "none",
                            color: "#0288d1",
                          }}
                        >
                          <strong> View Staged Order</strong>
                        </Grid> */}
                      </Grid>
                      <Grid container style={{ padding: "6px 16px" }}>
                        <Grid item xs={6}>
                          <strong>
                            {customerStore?.company_location_data[0]
                              ?.fuel_surcharge
                              ? "Subtotal:"
                              : "Total:"}
                          </strong>
                        </Grid>
                        <Grid item xs={6}>
                          $
                          {cartStore[customerStore.id].total
                            ? cartStore[customerStore.id].total.toFixed(2)
                            : 0}
                        </Grid>
                        {customerStore?.company_location_data[0]
                          ?.fuel_surcharge ? (
                          <>
                            <Grid item xs={6}>
                              <strong>Refrigerated Delivery Fee:</strong>
                            </Grid>
                            <Grid item xs={6}>
                              $
                              {(
                                customerStore?.company_location_data[0]
                                  ?.refrigerated_delivery_fee || 0
                              ).toFixed(2)}
                            </Grid>
                            <Grid item xs={6}>
                              <strong>Grand Total:</strong>
                            </Grid>
                            <Grid item xs={6}>
                              $
                              {(
                                cartStore[customerStore.id]?.total +
                                (customerStore?.company_location_data[0]
                                  ?.refrigerated_delivery_fee || 0)
                              ).toFixed(2)}
                            </Grid>
                          </>
                        ) : (
                          <></>
                        )}
                      </Grid>
                      {cartStore[customerStore.id].products.map(
                        (product, index) => (
                          <Grid
                            container
                            key={index}
                            style={{ padding: "6px 16px" }}
                          >
                            <Grid item xs={12}>
                              <strong>{product.name}</strong>
                            </Grid>
                            <Grid item xs={6}>
                              <strong>Unit: </strong>
                              {product.display_unit_price > 0
                                ? `$${product.display_unit_price} (${product.unit_in_cart})`
                                : `N/A`}
                            </Grid>
                            <Grid item xs={6}>
                              <strong>Case: </strong>
                              {product.display_case_price > 0
                                ? `$${product.display_case_price} (${product.case_in_cart})`
                                : `N/A`}
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Divider />
                            </Grid>
                          </Grid>
                        ),
                      )}
                    </>
                  ) : (
                    <>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        style={{ minWidth: 250, textAlign: "center" }}
                      >
                        Cart empty
                      </Grid>
                    </>
                  )}
                </>
              ) : (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  style={{ minWidth: 250, textAlign: "center" }}
                >
                  Cart empty
                </Grid>
              )}
            </div>
          </Popover>
          <IconButton
            color="inherit"
            aria-label="Logout"
            onClick={handleLogout}
          >
            {React.createElement(Icons["ExitToApp"])}
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        className={classes.drawer}
        color="secondary"
        variant="persistent"
        anchor="left"
        open={open}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <IconButton onClick={handleDrawer}>
            {theme.direction === "ltr"
              ? React.createElement(Icons["ChevronLeft"])
              : React.createElement(Icons["ChevronRight"])}
          </IconButton>
        </div>
        <Divider />
        {currentUserStore ? (
          <List key={"list"}>
            {linksData
              .filter((item) => checkPermissions(item.to))
              .map((item, index) => (
                <ListItem
                  button
                  key={index}
                  component={Link}
                  to={"/" + item.to}
                  className={item.className ? item.className : undefined}
                >
                  <ListItemIcon>
                    {React.createElement(Icons[item.icon])}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" color="white">
                        {item.text}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            {Boolean(
              customerStore?.company_location_data?.[0]
                ?.customer_editable_orderlist_enabled,
            ) &&
              customerStore?.company_location?.length > 0 && (
                <ListItem
                  button
                  component={Link}
                  to={
                    customerStore.company_location?.[0]
                      ? `/customer/${customerStore.company_location[0]}/orderlists`
                      : ""
                  }
                  className="order-guide"
                >
                  <ListItemIcon>
                    {React.createElement(Icons["Store"])}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" color="white">
                        My Order Guide
                      </Typography>
                    }
                  />
                </ListItem>
              )}
          </List>
        ) : (
          <></>
        )}
      </Drawer>
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: open,
        })}
        style={DEBUG ? { position: "relative", overflow: "hidden" } : undefined}
      >
        {DEBUG && (
          <div className={classes.devWatermark}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} style={{ width: "100%", textAlign: "center" }}>
                DEV DEV DEV DEV DEV
              </div>
            ))}
          </div>
        )}
        <div
          className={classes.drawerHeader}
          style={{
            display: printMode ? "none" : "inherit",
          }}
        />
        {!currentUserStore ? (
          <Typography align="center" style={{ marginTop: 40 }}>
            <CircularProgress />
            <br />
            Loading profile...
          </Typography>
        ) : checkPermissionsPath(path) ? (
          children
        ) : (
          <Typography align="center" style={{ marginTop: 40 }}>
            You are not authorized to view this page.
          </Typography>
        )}
      </main>
    </div>
  );
};

export default AppNavBar;
